import {
  CompassIcon,
  ClockIcon,
  HelpCircleIcon,
  BriefcaseIcon,
  RotateCcwIcon,
  FrownIcon,
  FileTextIcon,
  ClipboardCheckIcon,
  GitBranchIcon,
  Grid3x3Icon,
  FolderOpenIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export const CHECKOUT_URL = 'https://hernia-en.impultienda.ar/checkout/';

export const images = {
  mockup: 'https://cdn.magicpatterns.com/uploads/fYzW6rGHGbvqUaqrryHWaT/HERNIA-EN.png',
  author: 'https://cdn.magicpatterns.com/uploads/88A5LfFywxxKNzz5ZkdPUe/RYAN_CARTER_-_SPINE.png',
};

export const beforeItems = [
  'You are unsure where to begin your assessment.',
  'You jump between notes, courses, and protocols before seeing a patient.',
  'You struggle to organize all the information you collect.',
  'You hesitate when deciding what should be prioritized during the first session.',
  'You improvise your session structure depending on the patient.',
  'You feel like you have the knowledge — but not a system for applying it.',
];

export const afterItems = [
  'You follow a clear assessment structure.',
  'You use professional checklists instead of relying entirely on memory.',
  'You organize your findings before planning your intervention.',
  'You use decision algorithms to structure your clinical reasoning.',
  'You have a session framework you can quickly consult.',
  'You build a repeatable process for different clinical scenarios.',
];

export type PainPoint = { icon: LucideIcon; text: string };

export const painPoints: PainPoint[] = [
  {
    icon: CompassIcon,
    text: 'You have a patient with low back pain and symptoms consistent with a disc-related presentation — and you’re not exactly sure how to structure your initial assessment.',
  },
  {
    icon: ClockIcon,
    text: 'You spend too much time reviewing notes and searching for information before preparing a session.',
  },
  {
    icon: HelpCircleIcon,
    text: 'You have the physiotherapy knowledge, but still hesitate when you need to turn that knowledge into a practical assessment and treatment structure.',
  },
  {
    icon: BriefcaseIcon,
    text: 'You want to feel more prepared when working with complex cases and have a professional framework you can refer to in your practice.',
  },
  {
    icon: RotateCcwIcon,
    text: 'You feel like every patient forces you to start from scratch.',
  },
  {
    icon: FrownIcon,
    text: 'Before seeing a disc herniation patient, you experience more uncertainty than you would like.',
  },
];

export type Volume = {
  numeral: string;
  title: string;
  accent: string;
  description: string[];
  items: string[];
};

export const volumes: Volume[] = [
  {
    numeral: 'I',
    title: 'The Clinical Assessment Framework',
    accent: '#2563EB',
    description: [
      'Learn how to structure the assessment of a patient with a disc herniation using a clear and repeatable framework.',
      'Learn what information to collect, how to organize your findings, and which factors to consider before moving into treatment planning.',
    ],
    items: [
      'Initial assessment framework',
      'Patient history checklist',
      'Symptom organization framework',
      'Key assessment considerations',
      'Examination checklist',
      'Red-flag and referral considerations',
      'Clinical assessment worksheet',
      'Clinical reasoning template',
    ],
  },
  {
    numeral: 'II',
    title: 'The Clinical Decision-Making System',
    accent: '#16A34A',
    description: [
      'Stop keeping every clinical decision in your head and use a visual system to organize your reasoning.',
      'A practical framework designed to help you move from clinical findings toward a more organized treatment plan.',
    ],
    items: [
      'Clinical decision algorithm',
      'Decision tree',
      'Priority matrix',
      'Scenario classification framework',
      'Key clinical questions',
      'Safety filters',
      'Common reasoning mistakes',
      'Pre-treatment planning checklist',
    ],
  },
  {
    numeral: 'III',
    title: 'The Treatment Planning Playbook',
    accent: '#D97706',
    description: [
      'Turn your assessment findings into a structured treatment plan you can actually use.',
      'Learn how to organize goals, priorities, progression, and reassessment criteria in a systematic way.',
    ],
    items: [
      'Goal-setting framework',
      'Clinical priority system',
      'Session planning framework',
      'Phase-based planning',
      'Progression criteria',
      'Reassessment criteria',
      'Progress tracking',
      'Treatment planning templates',
    ],
  },
  {
    numeral: 'IV',
    title: 'The Session-by-Session System',
    accent: '#DC2626',
    description: [
      'Have a clear framework for organizing your sessions instead of improvising every time a patient walks through the door.',
      'A practical structure for organizing the process from the initial consultation through reassessment.',
    ],
    items: [
      'First-session framework',
      'Follow-up session structure',
      'Session planning checklist',
      'Pre-session checklist',
      'Post-session checklist',
      'Reassessment framework',
      'Progress tracking',
      'Criteria for modifying the plan',
    ],
  },
  {
    numeral: 'V',
    title: 'The Clinical Mistakes & Troubleshooting Guide',
    accent: '#7C3AED',
    description: [
      'Learn how to recognize common mistakes that can disrupt your clinical process before they become bigger problems.',
      'A practical review system for identifying potential gaps in assessment, planning, progression, and follow-up.',
    ],
    items: [
      'Common assessment mistakes',
      'Treatment planning mistakes',
      'Progression mistakes',
      'Follow-up mistakes',
      'Patient communication mistakes',
      'Clinical review checklist',
      'Troubleshooting matrix',
      'Practical case analysis',
    ],
  },
  {
    numeral: 'VI',
    title: 'The Professional Patient Communication Playbook',
    accent: '#0B86BD',
    description: [
      'Learn how to explain your clinical reasoning clearly and professionally so your patients understand what you’re assessing and why.',
      'Because a good assessment doesn’t end when you collect the information. You also need to communicate what you’re doing and what comes next.',
    ],
    items: [
      'First-consultation script',
      'Strategic patient questions',
      'How to explain your assessment',
      'How to present treatment goals',
      'How to explain the treatment plan',
      'Expectation-setting framework',
      'Scripts for common patient questions',
      'Follow-up communication templates',
    ],
  },
];

export type Tool = { icon: LucideIcon; title: string; text: string };

export const tools: Tool[] = [
  {
    icon: FileTextIcon,
    title: 'Quick-Reference Clinical Protocols',
    text: 'Practical resources designed to help you prepare for, structure, and review your sessions.',
  },
  {
    icon: ClipboardCheckIcon,
    title: 'Professional Checklists',
    text: 'Tools designed to help standardize your workflow and reduce your dependence on memory.',
  },
  {
    icon: GitBranchIcon,
    title: 'Clinical Decision Algorithms',
    text: 'Visual frameworks that help you organize information and structure your clinical reasoning.',
  },
  {
    icon: Grid3x3Icon,
    title: 'Clinical Matrices',
    text: 'Practical tools for organizing findings, priorities, and reassessment criteria.',
  },
  {
    icon: FolderOpenIcon,
    title: 'Professional Templates',
    text: 'Ready-to-adapt documents for organizing your assessment, planning, and follow-up process.',
  },
];

export type Bonus = {
  number: number;
  title: string;
  value: number;
  accent: string;
  image: string;
  description: string;
  listLabel?: string;
  items?: string[];
};

export const bonuses: Bonus[] = [
  {
    number: 1,
    title: '40 Clinical Cases Workbook',
    value: 47,
    accent: '#DC2626',
    image: 'https://cdn.magicpatterns.com/uploads/374mCoNxbauWPJsz88Q3qH/BONUS_1_HERNIA-EN.png',
    description:
      'A collection of practical cases designed to help you practice your clinical reasoning before facing different scenarios in your own practice.',
    listLabel: 'Each case includes:',
    items: [
      'Clinical scenario',
      'Initial information',
      'Analysis questions',
      'Key considerations',
      'Reasoning framework',
      'Space for your own clinical analysis',
    ],
  },
  {
    number: 2,
    title: '15-Minute Assessment Checklist™',
    value: 37,
    accent: '#2563EB',
    image: 'https://cdn.magicpatterns.com/uploads/9949Fkf7ADs273uUQbC1M7/BONUS_2_HERNIA-EN.png',
    description:
      'A quick-reference tool designed to help you organize the essential information during your initial assessment. Use it as a practical guide to help you structure your evaluation and avoid overlooking important considerations.',
  },
  {
    number: 3,
    title: 'Clinical Mistakes Library',
    value: 47,
    accent: '#16A34A',
    image: 'https://cdn.magicpatterns.com/uploads/3ZMUpH5XULdHQ6tJndG4zu/BONUS_3_HERNIA-EN.png',
    description:
      'A visual library of common mistakes related to assessment, planning, progression, and follow-up.',
    listLabel: 'Learn how to identify:',
    items: [
      'What may be going wrong',
      'Why it may happen',
      'What to review',
      'Which questions to ask yourself',
      'How to reorganize your process',
    ],
  },
  {
    number: 4,
    title: 'Patient Communication Scripts',
    value: 37,
    accent: '#EA580C',
    image: 'https://cdn.magicpatterns.com/uploads/mDsoApmgBRDVZH9gkqNueP/BONUS_4_HERNIA-EN.png',
    description: 'Professional scripts designed to help you clearly explain:',
    items: [
      'What you found during the assessment',
      'What the findings mean',
      'What the treatment plan involves',
      'Why reassessment matters',
      'What expectations the patient should have',
      'What questions to ask during follow-up',
    ],
  },
  {
    number: 5,
    title: '30 Clinical Checklists Pack',
    value: 29,
    accent: '#7C3AED',
    image: 'https://cdn.magicpatterns.com/uploads/155niBx5z3tRJK69Td3ZCg/BONUS_5_HERNIA-EN.png',
    description: 'A collection of checklists covering different stages of your clinical workflow.',
    listLabel: 'Includes checklists for:',
    items: [
      'Initial consultation',
      'Assessment',
      'Treatment planning',
      'Session preparation',
      'Reassessment',
      'Follow-up',
      'Patient communication',
      'Documentation',
    ],
  },
];

export const guaranteeChecks = [
  'Explore all 6 volumes.',
  'Review the protocols.',
  'Use the checklists.',
  'Work through the clinical cases.',
  'Test the practical tools.',
];

export type Testimonial = {
  name: string;
  role: string;
  country: string;
  photo: string;
  quote: string;
};

export const testimonials: Testimonial[] = [
  {
    name: 'Isabella Gomez',
    role: 'DPT',
    country: 'United States',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/d8d0367c-a1f6-4b26-94af-52e29c0c1c14.jpg',
    quote:
      'Before the Playbook I opened three different course notes before every new disc patient. Now I follow the same assessment framework every time, and my first sessions feel organized from minute one.',
  },
  {
    name: 'James O’Connor',
    role: 'Physical Therapist',
    country: 'Ireland',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/20d394ff-0283-4b9b-b73c-bf536226af48.jpg',
    quote:
      'The checklists are what sold me. I keep the 15-Minute Assessment Checklist open on my tablet and I no longer worry about skipping important screening questions.',
  },
  {
    name: 'Emily Turner',
    role: 'Physiotherapist',
    country: 'United Kingdom',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/c5cd9b9d-e5c2-4d12-a236-9f13600cea4d.jpg',
    quote:
      'As a newer clinician, the clinical cases were gold. Working through the reasoning framework before seeing similar patients gave me a lot more confidence.',
  },
  {
    name: 'Marcus Johnson',
    role: 'DPT',
    country: 'United States',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/4f0fdaf2-d2a7-40d8-9551-6abef58acfd9.jpg',
    quote:
      'The session-by-session system changed how I run follow-ups. I know what to reassess, what to track and when to modify the plan. No more improvising.',
  },
  {
    name: 'Sarah Mitchell',
    role: 'Physiotherapist',
    country: 'Australia',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/99b268bc-a4d7-460a-b8ae-b3490be12b43.jpg',
    quote:
      'Clear, practical and easy to come back to. It turned everything I already knew into a process I can repeat with every patient.',
  },
];

export const authorQuestions = [
  'Where do I start?',
  'What information should I prioritize?',
  'How should I organize my findings?',
  'How should I structure the session?',
  'What should I reassess?',
  'When should I reconsider my plan?',
];

export type Faq = { question: string; answer: string };

export const faqs: Faq[] = [
  {
    question: 'How will I receive the material?',
    answer:
      'Immediately after completing your purchase, you’ll receive instructions to access and download the 6 volumes and 5 included bonuses.',
  },
  {
    question: 'Is the content designed for physical therapists?',
    answer:
      'Yes. The Hernia Clinical Playbook™ was designed specifically as a professional educational and workflow-support resource for physical therapists.',
  },
  {
    question: 'Do I need to be a specialist?',
    answer:
      'Not necessarily. The Playbook is designed as a professional support resource for physical therapists. You should always work within your professional training, scope of practice, and applicable local regulations.',
  },
  {
    question: 'Does this replace my clinical education?',
    answer:
      'No. This is an educational and professional support resource. It does not replace formal education, clinical supervision, professional guidelines, or clinical judgment.',
  },
  {
    question: 'Are the files downloadable?',
    answer:
      'Yes. The materials are delivered digitally and can be downloaded for your personal professional reference.',
  },
  {
    question: 'Is this a one-time payment?',
    answer: 'Yes. You pay $27 once and receive access to the product according to the terms of the offer.',
  },
  {
    question: 'What if I don’t like it?',
    answer:
      'You have a 7-day guarantee. If the product doesn’t meet your expectations, you can request a refund according to the guarantee terms.',
  },
];

export type Buyer = { name: string; city: string; country: string };

export const buyers: Buyer[] = [
  { name: 'María G.', city: 'Bogotá', country: 'Colombia' },
  { name: 'Alejandro R.', city: 'Mexico City', country: 'Mexico' },
  { name: 'Lucía F.', city: 'Buenos Aires', country: 'Argentina' },
  { name: 'Javier M.', city: 'Madrid', country: 'Spain' },
  { name: 'Camila S.', city: 'Santiago', country: 'Chile' },
  { name: 'Diego P.', city: 'Lima', country: 'Peru' },
  { name: 'Valentina C.', city: 'Montevideo', country: 'Uruguay' },
  { name: 'Andrés L.', city: 'Quito', country: 'Ecuador' },
  { name: 'Sofía H.', city: 'San José', country: 'Costa Rica' },
  { name: 'Carlos V.', city: 'Santo Domingo', country: 'Dominican Republic' },
  { name: 'Gabriela T.', city: 'Guatemala City', country: 'Guatemala' },
  { name: 'Fernando A.', city: 'Panama City', country: 'Panama' },
  { name: 'Daniela O.', city: 'Asunción', country: 'Paraguay' },
  { name: 'Ricardo B.', city: 'San Juan', country: 'Puerto Rico' },
  { name: 'Paula E.', city: 'Barcelona', country: 'Spain' },
  { name: 'Mateo N.', city: 'Guadalajara', country: 'Mexico' },
  { name: 'Natalia J.', city: 'Medellín', country: 'Colombia' },
  { name: 'Sebastián D.', city: 'Córdoba', country: 'Argentina' },
];
